import { FormStore, GlobalStore, UserService } from '@roc/feature-app-core';
import { getDaysBetween, isNil, PURCHASE } from '@roc/feature-utils';
import { formatPercentage } from '@roc/ui/utils';
import { FixFlipGroundUpService } from 'libs/feature-loans/src/loanSubmission/fixflipGroundUp/Services/fixflipGroundUpService';
import { action, computed, makeObservable, observable } from 'mobx';
import { AmortizationType, AMORTIZED_DURATION, EMPTY_FEE_NAME_MESSAGE, FeeNames } from '../../utils/constants';
import { calculateMonthlyPaymentAmortizing, calculateMonthlyPaymentNonAmortizing, sum } from '../../utils/utils';
import { FeeFormStore } from '../common/feeFormStore';
import { LeverageFormStore } from '../fixFlip/leverageFormStore';
import { SinglePropertyRentalStore } from '../singlePropertyRentalStore';

const loanEconomicsForm = {
  fields: {
    loanTermMonths: {
      value: 12,
      error: null,
      rule: '',
    },
    amortizationType: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required',
    },
    rateType: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required',
    },
    prePaymentPenalty: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required',
    },
    loanAmount: {
      value: 0,
      error: null,
      rule: 'required',
      message: 'This field is required',
    },
    interestRate: {
      value: 0,
      error: null,
      rule: 'required',
      message: 'This field is required',
    },
    estimatedMonthlyPayment: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required',
    },
    brokerPoints: {
      value: 0,
      error: null,
      rule: 'required',
      message: 'This field is required',
    },
    //
    interestAccrualMethod: {
      value: 'As Disbursed',
      error: null,
      rule: 'required',
      message: 'This field is required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};


export class SinglePropertyLoanEconomicsStore {
  protected globalStore: GlobalStore;
  public rateAdjustmentsData: any;

  quoteTypeStore: SinglePropertyRentalStore;

  interestReserveFeeStore: FeeFormStore;
  thirdPartyCostsFeeStore: FeeFormStore;
  originationPointsFeeStore: FeeFormStore;
  feeStores: FeeFormStore[];
  loanEconomicsFormStore: FormStore;

  leverageFormStore: LeverageFormStore;

  interestReserveFinanced: boolean;
  showFinancedInterestReserve: boolean;

  validatedInitialLoanAmount: number;
  instaConstructionHoldback: number;
  manualInitialLoanAmount: number;
  manualConstructionHoldback: number;

  defaultFees: any;

  constructor(
    globalStore: GlobalStore,
    quoteTypeStore: SinglePropertyRentalStore
  ) {
    this.globalStore = globalStore;
    this.quoteTypeStore = quoteTypeStore;
    this.loanEconomicsFormStore = new FormStore(loanEconomicsForm, globalStore);

    this.leverageFormStore = new LeverageFormStore(globalStore);

    this.interestReserveFeeStore = new FeeFormStore(
      globalStore,
      this,
      FeeNames.INTEREST_RESERVE
    );
    this.thirdPartyCostsFeeStore = new FeeFormStore(
      globalStore,
      this,
      FeeNames.THIRD_PARTY_FEES
    );
    this.originationPointsFeeStore = new FeeFormStore(
      globalStore,
      this,
      FeeNames.ORIGINATION_POINTS,
      true
    );

    makeObservable(this, {
      reset: action,
      feeStores: observable,
      instaConstructionHoldback: observable,
      manualInitialLoanAmount: observable,
      manualConstructionHoldback: observable,
      rateAdjustmentsData: observable,
      calculateFields: action,
      calculateKeyRatios: action,
      loadLoanEconomics: action,
      getLoanEconomics: action,
      showFinancedInterestReserve: observable,
      interestReserveFinanced: observable,
      validateLeverageStep: action,
      validateLoanEconomicsStep: action,
    });
  }

  reset() {
    this.interestReserveFinanced = false;
    this.feeStores = [];
    this.rateAdjustmentsData = {};
    this.leverageFormStore.reset();
    this.validatedInitialLoanAmount = 0;
    this.instaConstructionHoldback = 0;
    this.manualInitialLoanAmount = 0;
    this.manualConstructionHoldback = 0;

    this.interestReserveFeeStore.reset();
    this.thirdPartyCostsFeeStore.reset();
    this.originationPointsFeeStore.reset();
  }

  loadLoanEconomics(quoteData) {
    const data = quoteData?.loanEconomics;
    if (!isNil(data)) {
      const fees = data.fees || [];
      this.feeStores = [];

      this.interestReserveFeeStore.loadForm({
        name: FeeNames.INTEREST_RESERVE,
        usePercentage: false,
        lenderAmount: data.amounts.interestReserve ?? 0,
      })
      this.thirdPartyCostsFeeStore.loadForm({
        name: FeeNames.THIRD_PARTY_FEES,
        usePercentage: false,
        lenderAmount: data.amounts.thirdParyCosts ?? 0,
      })
      this.originationPointsFeeStore.loadForm({
        name: FeeNames.ORIGINATION_POINTS,
        usePercentage: true,
        lenderPoints: data.amounts.originationPoints ?? 0,
      })
      fees.forEach(fee => {
        const form = new FeeFormStore(this.globalStore, this);
        form.loadForm(fee);
        this.feeStores.push(form);
      });

      this.leverageFormStore.loadForm({
        loanToCost: data.leverage?.loanToCost ?? 0,
        constructionReserve: data.leverage?.constructionReserve ?? 0,
      });
      this.loanEconomicsFormStore.loadForm({
        loanTermMonths: data.terms?.loanTermMonths ?? 12,
        totalPoints: data.totalPoints ?? 2,
        brokerPoints: quoteData.broker?.brokerPoints ?? 0,
        amortizationType: data.terms?.amortization,
        rateType: data.rates?.rateType,
        loanAmount: data.amounts?.totalLoanAmount,
        estimatedMonthlyPayment: data.amounts?.monthlyPayment,
        interestRate: data.rates?.interestRate,
        prePaymentPenalty: data.terms?.prePaymentPenalty,
      });
    } else {
      this.reset();
    }
  }

  getLoanEconomics() {
    const calculatedFields = this.calculateFields();
    const loanEconomicsData = this.loanEconomicsFormStore.getFormValues();
    const fees = this.feeStores
      .filter(form => !form.isEmpty)
      .map(form => form.getFeeValues(calculatedFields.totalLoanAmount));

    return {
      totalPoints: loanEconomicsData.totalPoints,
      dscr: calculatedFields.pitiaDSCR,
      leverage: {
        loanToValue: calculatedFields.loanToValue,
      },
      amounts: {
        totalLoanAmount: calculatedFields.totalLoanAmount, // Calculated
        initialLoanAmount: calculatedFields.initialAdvance, // Calculated
        monthlyPayment: calculatedFields.monthlyPIPayment, // Calculated
        interestReserve: this.interestReserveFeeStore.form.fields.lenderAmount.value,
        thirdParyCosts: this.thirdPartyCostsFeeStore.form.fields.lenderAmount.value,
        originationPoints: this.originationPointsFeeStore.form.fields.lenderPoints.value,
      },
      rates: {
        rateType: loanEconomicsData.rateType,
        interestRate: loanEconomicsData.interestRate,
      },
      terms: {
        amortization: loanEconomicsData.amortizationType,
        loanTermMonths: loanEconomicsData.loanTermMonths,
        prePaymentPenalty: loanEconomicsData.prePaymentPenalty,
      },
      fees: fees,
    };
  }

  getBroker() {
    const { brokerPoints } = this.loanEconomicsFormStore.form.fields;
    return brokerPoints.value > 0
      ? {
        hasBroker: true,
        brokerPoints: brokerPoints.value,
      }
      : null;
  }

  getCalculatedData() {
    const calculatedFields = this.calculateFields();
    return {
      totalPurchasePrice: calculatedFields.totalPurchasePrice,
    };
  }

  addNewFee() {
    this.feeStores.push(new FeeFormStore(this.globalStore, this));
  }

  removeFee(feeKey: string) {
    this.feeStores = this.feeStores.filter(fee => fee.key !== feeKey);
  }

  get totalLoanAmount() {
    const calculatedFields = this.calculateFields();
    return calculatedFields.totalLoanAmount;
  }

  calculateFields() {
    const loanEconomicsData = this.loanEconomicsFormStore.getFormValues();
    const properties = this.quoteTypeStore.propertiesStore.getProperties();

    const loanAmount = loanEconomicsData.loanAmount ?? 0;
    const brokerPoints = loanEconomicsData.brokerPoints ?? 0;
    const interestRate = loanEconomicsData.interestRate ?? 0;

    const totalSellerCredit = sum(
      properties.filter(p => !!p.sellerCredit).map(p => p.sellerCreditAmount)
    );

    let totalPurchasePrice = 0;
    if (properties?.[0]?.loanPurpose === PURCHASE) {
      totalPurchasePrice =
        sum(properties.map(p => p.financials.purchasePrice)) -
        totalSellerCredit;
    } else {
      totalPurchasePrice = sum(
        properties.map(p => p.financials.currentMarketValue)
      );
    }

    const totalMonthlyMarketRent = sum(
      properties.map(p => p.financials.monthlyMarketRent)
    );

    const totalAnnualTaxes = sum(properties.map(p => p.financials.annualTaxes));

    const totalAnnualInsurance = sum(
      properties.map(p => p.financials.annualInsurance)
    );

    const totalAnnualHOA = sum(properties.map(p => p.financials.annualHOA));

    const totalAnnualExpenses =
      totalAnnualTaxes + totalAnnualInsurance + totalAnnualHOA

    const totalMarketValue = sum(
      properties.map(p => p.financials.currentMarketValue)
    );

    const monthlyPIPayment = loanEconomicsData.amortizationType === AmortizationType.FULLY_AMORTIZING
      ? calculateMonthlyPaymentAmortizing(loanAmount, interestRate, AMORTIZED_DURATION)
      : calculateMonthlyPaymentNonAmortizing(loanAmount, interestRate);

    const monthlyPITIAPayment = monthlyPIPayment + totalAnnualExpenses / 12;



    const initialAdvance = loanAmount;
    const totalLoanAmount = loanAmount;
    const calculatedLTV = (totalLoanAmount / totalMarketValue) * 100;
    const loanToValue = (loanAmount / totalPurchasePrice) * 100;

    const brokerOriginationFees = (brokerPoints * totalLoanAmount) / 100;

    const interestReserve =
      this.interestReserveFeeStore.getFeeValues(totalLoanAmount).lenderAmount ??
      0;
    const thirdPartyCosts =
      this.thirdPartyCostsFeeStore.getFeeValues(totalLoanAmount).lenderAmount ??
      0;
    const originationFees =
      this.originationPointsFeeStore.getFeeValues(totalLoanAmount)
        .lenderAmount ?? 0;

    const lenderFees = this.feeStores.map(feeStore =>
      feeStore.getFeeValues(totalLoanAmount)
    );
    const totalLenderFees = sum(lenderFees.map(fee => fee.lenderAmount ?? 0)) + originationFees;

    const pitiaDSCR = monthlyPITIAPayment > 0 ? totalMonthlyMarketRent / monthlyPITIAPayment : 0;

    return {
      totalMarketValue,
      calculatedLTV,
      initialAdvance,
      totalLoanAmount,
      totalPurchasePrice,
      brokerOriginationFees,
      interestReserve,
      thirdPartyCosts,
      originationFees,
      totalLenderFees,
      monthlyPIPayment,
      monthlyPITIAPayment,
      pitiaDSCR,
      loanToValue,
    };
  }

  calculateKeyRatios() {
    return ``;
  }

  validateLeverageStep() {
    return true;
  }

  validateLoanEconomicsStep() {
    if (this.feeStores.some(f => f.isEmpty)) {
      this.globalStore.notificationStore.showErrorNotification({
        message: EMPTY_FEE_NAME_MESSAGE,
      });
      return false;
    }
    return true;
  }
}
